import type { ChangeType, OrganizationType } from '@shared/types';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const CHANGE_TYPE_OPTIONS: {
  value: ChangeType;
  label: string;
  description: string;
}[] = [
  { value: 'RENAME', label: '名称変更', description: '組織名を変更します' },
  {
    value: 'MERGE',
    label: '統合',
    description: '他の組織と統合し、この組織を廃止します',
  },
  { value: 'ABOLISH', label: '廃止', description: 'この組織を廃止します' },
  { value: 'MOVE', label: '階層移動', description: '親組織を変更します' },
];

// Mock current organization
const mockCurrentOrg = {
  id: 'dept-1',
  type: 'DEPARTMENT' as OrganizationType,
  nameFormal: 'Department of Computer Science',
  nameShort: 'CS',
};

// Mock available organizations for successor selection
const mockOrganizations = [
  {
    id: 'dept-2',
    name: 'Department of Information Engineering',
    type: 'DEPARTMENT',
  },
  { id: 'dept-3', name: 'Department of Data Science', type: 'DEPARTMENT' },
  { id: 'fac-1', name: 'Faculty of Engineering', type: 'FACULTY' },
  { id: 'fac-2', name: 'Faculty of Science', type: 'FACULTY' },
];

export default function ChangeRequestForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    changeType: '' as ChangeType | '',
    scheduledDate: '',
    proposedNameFormal: mockCurrentOrg.nameFormal,
    proposedNameShort: mockCurrentOrg.nameShort,
    proposedParentId: '',
    successorId: '',
    reason: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.changeType) {
      alert('変更種別を選択してください');
      return;
    }
    if (!formData.scheduledDate) {
      alert('適用予定日を入力してください');
      return;
    }
    if (!formData.reason.trim()) {
      alert('申請理由を入力してください');
      return;
    }

    console.log('Submitting change request:', formData);
    alert('変更予約を登録しました（モック）');
    navigate(`/admin/master/organizations/${id}`);
  };

  const getTodayString = () => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  };

  return (
    <div className="p-8 max-w-3xl">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/admin/master/organizations" className="hover:text-blue-600">
          組織マスタ
        </Link>
        <span>/</span>
        <Link to={`/admin/master/organizations/${id}`} className="hover:text-blue-600">
          {mockCurrentOrg.nameFormal}
        </Link>
        <span>/</span>
        <span className="font-medium text-gray-900">変更予約登録</span>
      </nav>

      {/* Header */}
      <h1 className="text-2xl font-bold mb-6">変更予約を登録</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Change Type Selection */}
        <section className="bg-white border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">変更種別</h2>
          <div className="grid grid-cols-2 gap-3">
            {CHANGE_TYPE_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                  formData.changeType === opt.value ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="changeType"
                  value={opt.value}
                  checked={formData.changeType === opt.value}
                  onChange={handleChange}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-sm text-gray-500">{opt.description}</div>
                </div>
              </label>
            ))}
          </div>
        </section>

        {/* Scheduled Date */}
        <section className="bg-white border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">適用予定日</h2>
          <input
            type="date"
            name="scheduledDate"
            value={formData.scheduledDate}
            onChange={handleChange}
            min={getTodayString()}
            className="border rounded-md px-3 py-2"
            required
          />
          <p className="text-sm text-gray-500 mt-2">
            この日付から変更が有効になります。通常は年度開始日（4月1日）を指定します。
          </p>
        </section>

        {/* Conditional Fields based on Change Type */}
        {formData.changeType === 'RENAME' && (
          <section className="bg-white border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">新しい名称</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="proposedNameFormal" className="block text-sm font-medium text-gray-700 mb-1">
                  正式名称
                </label>
                <input
                  type="text"
                  id="proposedNameFormal"
                  name="proposedNameFormal"
                  value={formData.proposedNameFormal}
                  onChange={handleChange}
                  className="w-full border rounded-md px-3 py-2"
                  required
                />
              </div>
              <div>
                <label htmlFor="proposedNameShort" className="block text-sm font-medium text-gray-700 mb-1">
                  短縮名称
                </label>
                <input
                  type="text"
                  id="proposedNameShort"
                  name="proposedNameShort"
                  value={formData.proposedNameShort}
                  onChange={handleChange}
                  className="w-full border rounded-md px-3 py-2"
                  required
                />
              </div>
            </div>
          </section>
        )}

        {(formData.changeType === 'MERGE' || formData.changeType === 'ABOLISH') && (
          <section className="bg-white border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">引継ぎ先組織</h2>
            <select
              name="successorId"
              value={formData.successorId}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
              required={formData.changeType === 'MERGE'}
            >
              <option value="">選択してください</option>
              {mockOrganizations
                .filter((org) => org.id !== id)
                .map((org) => (
                  <option key={org.id} value={org.id}>
                    [{org.type}] {org.name}
                  </option>
                ))}
            </select>
            <p className="text-sm text-gray-500 mt-2">
              {formData.changeType === 'MERGE'
                ? '統合後、この組織の学生は選択した組織に引き継がれます。'
                : '廃止後、この組織の学生を引き継ぐ組織を指定します（任意）。'}
            </p>
          </section>
        )}

        {formData.changeType === 'MOVE' && (
          <section className="bg-white border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">新しい親組織</h2>
            <select
              name="proposedParentId"
              value={formData.proposedParentId}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
              required
            >
              <option value="">選択してください</option>
              {mockOrganizations
                .filter((org) => org.type === 'FACULTY')
                .map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
            </select>
            <p className="text-sm text-gray-500 mt-2">この組織を配下に移動させる親組織を選択します。</p>
          </section>
        )}

        {/* Reason */}
        <section className="bg-white border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">申請理由</h2>
          <textarea
            name="reason"
            value={formData.reason}
            onChange={handleChange}
            className="w-full border rounded-md px-3 py-2 h-24"
            placeholder="変更の理由を記入してください（必須）"
            required
          />
          <p className="text-sm text-gray-500 mt-2">承認者がこの内容を確認して承認可否を判断します。</p>
        </section>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(`/admin/master/organizations/${id}`)}>
            キャンセル
          </Button>
          <Button type="submit">予約を登録</Button>
        </div>
      </form>
    </div>
  );
}
